import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { Flex } from '@/components/Flex';
import { Grid } from '@/components/Grid';
import { Icon } from '@/components/Icon';
import { Typography } from '@/components/Typography';
import { useCustomSignOut } from '@/hooks/useCustomSignOut';
import { useRouter } from '@/hooks/useRouter';
import { allowedCountries } from '@/lib/eccomerceCountries';
import { paths } from '@/lib/paths';
import { getRegionFromLocale } from '@/lib/regions';
import type { WithChildren } from '@/lib/types';
import { myAccountMessages } from '@/messages/myAccount';
import { useConfigProvider } from '@/providers/ConfigProvider';

import {
  MY_ADDRESSES_MENU,
  MY_DETAILS_MENU,
  type NavigationType,
  ORDERS_MENU,
} from './consts';

import { ReactComponent as LogoutIcon } from '@/assets/my_account/icons/logout.svg';

import * as S from './SideNavigationMenu.styles';

interface SideNavigationMenuItemProps extends WithChildren<false> {
  activeIcon: JSX.Element;
  disabled?: boolean;
  icon: JSX.Element;
  label: JSX.Element;
  onClick?: (
    evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => Promise<void>;
  path: string;
}

const NavigationItem = ({
  activeIcon,
  disabled = false,
  icon,
  label,
  onClick,
  path,
}: SideNavigationMenuItemProps) => {
  const { asPath } = useRouter();
  const isActive =
    (`${path}/` === asPath || asPath?.includes(path)) &&
    path !== paths.home.asPath();

  return (
    <S.StyledLink
      $isActive={isActive}
      $underline={false}
      data-test-id='SideNavigationMenuItem'
      disabled={disabled}
      href={path}
      onClick={onClick}
    >
      <S.NavigationItem data-test-id='SideNavigationItem'>
        <Flex alignItems='center' gridGap={2}>
          {isActive ? activeIcon : icon}
          {label}
        </Flex>
        <Icon name='chevronRight' />
      </S.NavigationItem>
    </S.StyledLink>
  );
};

interface SideNavigationMenuProps {
  navigationType: NavigationType;
}

const menu_type_map = {
  myDetails: MY_DETAILS_MENU,
  myOrders: ORDERS_MENU,
  myAddresses: MY_ADDRESSES_MENU,
};
export const getFilteredMenuByCountry = (
  countryCode: string,
  navigationType: NavigationType,
) => {
  return menu_type_map[navigationType]?.filter((item) => {
    if (item.path === paths.myAccount.myAddresses.history.asPath()) {
      return allowedCountries?.includes(countryCode);
    }
    return true;
  });
};
export const SideNavigationMenu = ({
  navigationType,
}: SideNavigationMenuProps) => {
  const { ecsConfig } = useConfigProvider();
  const { locale: routerLocale } = useRouter();
  const { country } = getRegionFromLocale(routerLocale);
  const countryCode = country.slug.toUpperCase();
  const menu = getFilteredMenuByCountry(countryCode, navigationType);
  const { signOut } = useCustomSignOut();
  const { push } = useRouter();
  const { formatMessage } = useIntl();
  const handleSignOutClick = useCallback(
    async (evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      evt.preventDefault();

      await signOut();
      await push(paths.home.asPath());
    },
    [],
  );

  return (
    <Grid data-test-id='SideNavigationMenu'>
      {menu.map((item, index) => {
        if (
          item.path === paths.myAccount.myOrders.serviceHistory.asPath() &&
          (!ecsConfig?.ecsAvailable || !ecsConfig.isNewEcsSystem)
        ) {
          return null;
        }
        return (
          <NavigationItem
            key={item.path}
            activeIcon={item.activeIcon}
            icon={item.icon}
            label={
              <Typography
                data-test-id={`SideNavigationLabel-${index}`}
                fontWeight='bold'
                textTransform='uppercase'
              >
                {item.label}
              </Typography>
            }
            path={item.path}
          />
        );
      })}

      <NavigationItem
        activeIcon={<LogoutIcon height={24} />}
        icon={<LogoutIcon height={24} />}
        label={
          <Typography
            data-test-id='SideNavigationLogoutLabel'
            fontWeight='bold'
            textTransform='uppercase'
          >
            {formatMessage(myAccountMessages.accountDetailsMenuLogOut)}
          </Typography>
        }
        onClick={handleSignOutClick}
        path={paths.home.asPath()}
      />
    </Grid>
  );
};

SideNavigationMenu.displayName = 'SideNavigationMenu';
